import {Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {classToClass, plainToClass} from "class-transformer";
import {ConfirmationService} from "primeng/api";
import {OverlayPanel} from "primeng/overlaypanel";
import {DwollaMessages} from "@app/shared/ach/dwolla-messages.enum";
import {Format} from '@app/shared/enums/format.enum';
import {PaymentStatusType} from "@app/shared/enums/payment-status-type.enum";
import {PaymentTxnType} from "@app/shared/enums/txn-type.enum";
import {PaymentType} from "@app/shared/enums/payment-type.enum";
import {RouteLink} from "@app/shared/enums/route-link.enum";
import {PaymentStatusRequest} from "@app/shared/ach/payment-status/payment-status-request";
import {AchError, OrgPaymentTransaction} from "@app/shared/ach/org-payment-transaction";
import {CommonDocRepoService} from "@app/shared/services/common-doc-repo.service";
import {AchDataService} from "@app/shared/ach/ach-data.service";
import {VoucherRegister} from "@app/shared/classes/voucher-register";
import {PrimeNgConfig, TableColumn, TableFormat} from "@app/shared";

@Component({
  selector: 'app-payment-status',
  templateUrl: './payment-status.component.html',
  styleUrls: ['./payment-status.component.css'],
  providers: [AchDataService]
})
export class PaymentStatusComponent implements OnInit {

  // standard format and validation
  Format: typeof Format = Format;
  PaymentStatusType: typeof PaymentStatusType = PaymentStatusType;
  PaymentTxnType: typeof PaymentTxnType = PaymentTxnType;
  PaymentType: typeof PaymentType = PaymentType;
  RouteLink: typeof RouteLink = RouteLink;
  PrimeNgConfig: typeof PrimeNgConfig = PrimeNgConfig;

  orgPaymentTransaction: OrgPaymentTransaction;
  showApprovalStatusDialog: boolean;
  _request: PaymentStatusRequest;       // jC: made it public since used in the html
  achError: AchError;
  voucherRegisters: VoucherRegister[];
  voucherRegisterTableHeaders: TableColumn[];
  displayCancelOption: boolean = false;
  @Output() retryFailedEvent = new EventEmitter<OrgPaymentTransaction[]>();

  //Dwolla Messages
  cancelToolTipInfo: string;

  @Input()
  set request(value: PaymentStatusRequest) {
    if (value) {
      console.debug("payment-status:set request =" + value);
      this._request = value;
      this.achError = null;
      this.getPaymentStatus(value);
    } else {
      console.debug("payment-status:set request = undefined value");
    }
  }


  constructor(private dataService: AchDataService, private fileService: CommonDocRepoService, private confirmationService: ConfirmationService) {
  }

  ngOnInit() {
    this.achError = null;
    this.populateDwollaMessages();
  }

  populateTableHeaders(request: PaymentStatusRequest) {
    if (request.paymentTransaction == PaymentTxnType.VoucherRegister) {
      this.voucherRegisterTableHeaders = [
        {field: 'invoiceNumber', header: 'Invoice #', format: TableFormat.RouteLink, routeLink: RouteLink.VendorInvoice, refIdField: 'invoiceId'},
        {field: 'vendorName', header: 'Vendor Name'},
        {field: 'invoicePaymentNumber', header: 'Invoice payment #'},
        {field: 'paymentType', header: 'Payment Type', format: TableFormat.TitleCase},
        {field: 'paymentAmount', header: 'Amount', format: TableFormat.Currency}
      ];
    } else {
      this.voucherRegisterTableHeaders = [];
    }
  }

  populateDwollaMessages(){
    this.cancelToolTipInfo = DwollaMessages.CancelPaymentInfo;
  }


  get request(): PaymentStatusRequest {
    return this._request;
  }

  getPaymentStatus(request: PaymentStatusRequest) {
    if (request && request.paymentTransaction) {
      if (!request.txnHistory) {
        this.getSingleTransactionStatus(request);
      } else {
        this.populateTableHeaders(request);
        this.getMultipleTransactionStatus(request);
      }
    } else {
      console.error("approval-status: txnTypeId or txnTypeName is required");
    }
  }

  getSingleTransactionStatus(request: PaymentStatusRequest) {
    this.dataService.getPaymentTransactionStatus(request.refId, request.paymentTransaction).subscribe((res) => {
      console.log("Received Response");
      console.log(res);
      this.orgPaymentTransaction = plainToClass(OrgPaymentTransaction, res as OrgPaymentTransaction);
      if (!this.orgPaymentTransaction.id) {
        this.orgPaymentTransaction = null;
      }
      this.checkCancelEligibility();
      this.showApprovalStatusDialog = true;
    });
  }

  getMultipleTransactionStatus(request: PaymentStatusRequest) {
    console.log(request);
    this.showApprovalStatusDialog = true;
    if (request.paymentTransaction == PaymentTxnType.VoucherRegister) {
      this.dataService.getVoucherRegisterForTransaction(request.dwollaId, request.paymentTransaction).subscribe(res => {
        this.orgPaymentTransaction = plainToClass(OrgPaymentTransaction, res.transaction as OrgPaymentTransaction);
        this.voucherRegisters = plainToClass(VoucherRegister, res.voucherRegisters);
        this.checkCancelEligibility();
      });
    }
  }


  refreshPaymentStatus(request: PaymentStatusRequest, transaction: OrgPaymentTransaction) {
    if (request && request.paymentTransaction) {
      //TODO: Handle multiple Txn status update
      this.dataService.updateTransactionStatusByReference(transaction).subscribe(res => {
        this.orgPaymentTransaction = plainToClass(OrgPaymentTransaction, res as OrgPaymentTransaction);
      });
    }
  }

  getPaymentFailureReason(event, request: PaymentStatusRequest, transaction: OrgPaymentTransaction, overlayPanel: OverlayPanel) {
    if (request && request.paymentTransaction) {
      this.dataService.getPaymentTransactionFailureReason(transaction).subscribe(res => {
        this.achError = plainToClass(AchError, res as AchError);
        //this.showErrorReasonDialog = true;
        overlayPanel.toggle(event);
      });
    }
  }

  retryPaymentForFailureTransaction(transaction: OrgPaymentTransaction) {
    this.dataService.retryPaymentForFailureTransaction(transaction).subscribe(res => {
      console.log(res);
      let updatedTxnList: OrgPaymentTransaction[] = plainToClass(OrgPaymentTransaction, res.list);
      //TODO: Send the updated Txn to the parent compenent as an event
      if (updatedTxnList.length > 0) {
        //If it is part of Txn History (Multiple entities then calculate the total Txn amount
        this.orgPaymentTransaction = classToClass(updatedTxnList[0]);
        this.orgPaymentTransaction.txnTotalAmount = this.calculateTotalTxnAmount(updatedTxnList);
        this.retryFailedEvent.emit(updatedTxnList);
      }
    });
  }

  cancelPayment(transaction: OrgPaymentTransaction) {

    this.confirmationService.confirm({
      message: 'Transaction cannot be cancelled if it is already in process, Please contact Customer support if cancellation fails. Do you wish to continue?',
      header: 'Cancel Confirmation',
      icon: 'fa fa-trash',
      key: 'cancelConfirmation',
      accept: () => {
        this.dataService.cancelPayment(transaction).subscribe(res => {
          let cancelledTxnList: OrgPaymentTransaction[] = plainToClass(OrgPaymentTransaction, res.list);
          let index = cancelledTxnList.findIndex(txn => txn.id == this.orgPaymentTransaction.id);
          if (index > -1) {
            this.orgPaymentTransaction = classToClass(cancelledTxnList[index]);
          } else {
            console.warn("Transaction Not found");
          }
        });
      }
    });
  }

  calculateTotalTxnAmount(txnList: OrgPaymentTransaction[]) {
    let totalAmount = 0;
    txnList.forEach(txn => {
      totalAmount = totalAmount + txn.paymentAmount;
    });
    return totalAmount;
  }

  close() {
    this.showApprovalStatusDialog = false;
    this.achError = null;
  }

  onClickDownloadCheck(paymentFileId: number) {
    if (paymentFileId) {
      this.fileService.getFileByIdThenSave(paymentFileId);
    }
  }

  checkCancelEligibility(){

    if (this.orgPaymentTransaction && this.orgPaymentTransaction.paymentType == PaymentType.ACH && this.orgPaymentTransaction.transactionId &&
      (this.orgPaymentTransaction.paymentStatus == PaymentStatusType.Submitted || this.orgPaymentTransaction.paymentStatus == PaymentStatusType.Pending
        || this.orgPaymentTransaction.paymentStatus == PaymentStatusType.Processing)) {
      this.displayCancelOption = true;
    } else {
      this.displayCancelOption = false;
    }


  }

}
